import { Button, Input, Typography, LoadingSpinnerIcon, CloseAltIcon } from '@mgmresorts/wcl-lab';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { OktaUser } from '../../../@types/okta-user';
import { KnownError, UNKNOWN_ERROR_MESSAGE } from '../../api/errors';
import { requestChangePassword } from '../../api/fetch/change-password';
import { Alert } from '../../components/ui/Alert';
import { Confirmation } from '../../components/ui/Confirmation';
import { Container } from '../../components/ui/Container';
import { Link } from '../../components/ui/Link';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import { useContent } from '../../hooks/useContent';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useInput } from '../../hooks/useInput';
import { usePasswordInput } from '../../hooks/usePasswordInput';
import { captureEvent } from '../../monitoring/logrocket';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../utils/auth/AuthProvider';
import { MaxLengths } from '../../utils/constants';
import { redirectToSignIn } from '../../utils/redirect';
import { PasswordValidationHint } from '../ResetPassword/styled';

import { StyledContent } from './styled';

export function ChangePassword() {
  usePageLoadEvent('sign in', 'change password');
  const trackEvent = useEvent();
  const [userInfo, setUserInfo] = useState<OktaUser | null>();
  const [error, setErrorState] = useState<null | string>(null);
  const [isLoading, setLoadingState] = useState(false);
  const { acquireToken, getUserInfo } = useAuthContext();
  const { validateField, invalidFields } = useFieldValidation();
  const [successfullyChanged, setSuccessfullyChanged] = useState(false);

  const { getContentByPath } = useContent();

  const defaultRedirect = `${window.location.origin}/account/profile`;

  const { bind: bindCurrentPassword, value: currentPassword } = useInput(
    '',
    'currentPassword',
    undefined,
    validateField,
  );

  const {
    bind: bindNewPassword,
    value: newPassword,
    isValid: isValidNewPassword,
    validations: passwordValidations,
  } = usePasswordInput('', 'newPassword', userInfo?.profile ?? {}, undefined, validateField);

  const { bind: bindReTypePassword, value: reTypePassword } = useInput('', 'reTypePassword', undefined, validateField);

  const resetPassword = useCallback(
    async e => {
      try {
        e.preventDefault();
        setLoadingState(true);

        captureEvent('Change Password', {
          email: userInfo?.profile?.login ?? 'undefined',
        });

        const token = await acquireToken();

        if (!token?.accessToken || token.isTransient) {
          setErrorState(UNKNOWN_ERROR_MESSAGE);
          return;
        }

        await requestChangePassword({
          oldPassword: currentPassword,
          newPassword,
        });

        setSuccessfullyChanged(true);
      } catch (error) {
        let message;
        if (error instanceof KnownError) {
          message = error.message;
        } else {
          message = UNKNOWN_ERROR_MESSAGE;
        }
        setErrorState(message);
      } finally {
        setLoadingState(false);
      }
    },
    [acquireToken, currentPassword, newPassword, userInfo?.profile?.login],
  );

  const passwordValidationHint = useCallback(
    (validation: keyof typeof passwordValidations, hint: string) => {
      const isValid = passwordValidations[validation];
      if (isValid) {
        return null;
      }
      const color = '#E02D21';
      const Icon = CloseAltIcon;
      if (!newPassword.length) {
        return (
          <PasswordValidationHint>
            <span style={{ margin: '0 6px' }}>●</span>
            <Typography variant='paragraph'>{hint}</Typography>
          </PasswordValidationHint>
        );
      }
      return (
        <PasswordValidationHint>
          <Icon color={color} size={20} width={40} />
          <Typography variant='paragraph' color={color}>
            {hint}
          </Typography>
        </PasswordValidationHint>
      );
    },
    [newPassword.length, passwordValidations],
  );

  const currentPasswordError = useMemo(
    () =>
      invalidFields.includes('currentPassword')
        ? getContentByPath<string>('changePassword.fields.currentPassword.error')
        : '',
    [invalidFields, getContentByPath],
  );

  const newPasswordError = useMemo(() => {
    if (invalidFields.includes('newPassword')) {
      return getContentByPath<string>('changePassword.fields.newPassword.error');
    }
    if (newPassword && currentPassword && newPassword === currentPassword) {
      return getContentByPath<string>('changePassword.fields.newPassword.errorNotEqual');
    }
    return '';
  }, [currentPassword, getContentByPath, invalidFields, newPassword]);

  const reTypePasswordError = useMemo(() => {
    if (invalidFields.includes('reTypePassword')) {
      return getContentByPath<string>('changePassword.fields.reTypePassword.error');
    }
    if (reTypePassword && newPassword && reTypePassword !== newPassword) {
      return getContentByPath<string>('changePassword.fields.reTypePassword.errorNotEqual');
    }
    return '';
  }, [getContentByPath, invalidFields, newPassword, reTypePassword]);

  const allRequirementsMet = useMemo(
    () =>
      currentPassword &&
      newPassword &&
      reTypePassword &&
      isValidNewPassword &&
      !currentPasswordError &&
      !reTypePasswordError &&
      !newPasswordError,
    [
      currentPassword,
      currentPasswordError,
      isValidNewPassword,
      newPassword,
      newPasswordError,
      reTypePassword,
      reTypePasswordError,
    ],
  );

  useEffect(() => {
    const errors = {
      'Old Password': currentPasswordError,
      'New Password': newPasswordError,
      'Re-Type Password': reTypePasswordError,
    };
    if (currentPasswordError || newPasswordError || reTypePasswordError) {
      trackEvent(
        'identity – update password - credentials input error',
        'update password credentials input error',
        'updatepinputerror',
        {
          errorField: Object.entries(errors)
            .filter(([, message]) => message)
            .map(([error]) => error)
            .join(','),
          errorDisplayed: Object.entries(errors)
            .filter(([, message]) => !!message)
            .map(([, message]) => message)
            .join(','),
        },
      );
    }
  }, [currentPasswordError, newPasswordError, reTypePasswordError, trackEvent]);

  useEffect(() => {
    if (error) {
      trackEvent(
        'identity – update password - credentials submit error',
        'update password credentials submit error',
        'updatepasswordsubmitfail',
        {
          updatePasswordState: 'failed',
          errorDisplayed: error,
        },
      );
    }
  }, [error, trackEvent]);

  useEffect(() => {
    if (successfullyChanged) {
      trackEvent(
        'identity – update password - credentials submit success',
        'update password credentials submit success',
        'updatepsubmitsuccess',
        {
          updatePasswordState: 'success',
        },
      );
    }
  }, [successfullyChanged, trackEvent]);

  useEffect(() => {
    getUserInfo().then(setUserInfo);
  }, [getUserInfo]);

  useEffect(() => {
    if (userInfo) {
      trackEvent('identity – update password - start', 'update password start', 'updatepasswordstart', {}, true);
    }
  }, [trackEvent, userInfo]);

  if (userInfo === undefined) {
    return (
      <PageLayout
        testId='route-/change-password'
        title={getContentByPath('changePassword.title')}
        description={getContentByPath('changePassword.description')}
      >
        <div data-testid='page-title' />
        <Container height='692px'>
          <Centered>
            <LoadingSpinnerIcon size='48' />
          </Centered>
        </Container>
      </PageLayout>
    );
  }

  if (successfullyChanged) {
    return (
      <Confirmation
        title={getContentByPath('changePassword.successfullyChanged.title')}
        description={getContentByPath<string>('changePassword.successfullyChanged.content')}
        redirectCTA={{
          label: getContentByPath('changePassword.successfullyChanged.button'),
          action: async () => {
            await trackEvent(
              'identity – update password - back to account',
              'back to account',
              'updatepbacktoaccount',
              {},
              true,
            );
            window.location.href = defaultRedirect;
          },
        }}
      />
    );
  }

  if (userInfo === null) {
    redirectToSignIn();
  }

  return (
    <StyledContent>
      <PageLayout
        title={getContentByPath('changePassword.title')}
        description={getContentByPath('changePassword.description')}
      >
        <Container height='auto'>
          <Logo className='identity__page-logo' />
          <Typography className='identity__page-heading' variant='h1' data-testid='page-title'>
            {getContentByPath('changePassword.title')}
          </Typography>

          {error && <Alert text={error} data-testid='change-password-error' />}
          <form className='identity__form'>
            <Input
              type='password'
              className={`identity__field password current ${currentPasswordError ? 'with-error' : ''}`}
              label={getContentByPath('changePassword.fields.currentPassword.title')}
              id='currentPassword'
              aria-label={getContentByPath('changePassword.fields.currentPassword.label')}
              disabled={isLoading}
              error={!!currentPasswordError}
              hint={currentPasswordError}
              {...bindCurrentPassword}
              maxLength={MaxLengths.Password}
            />

            <Input
              type='password'
              className={`identity__field password  ${newPasswordError ? 'with-error' : ''}`}
              label={getContentByPath('changePassword.fields.newPassword.title')}
              id='newPassword'
              aria-label={getContentByPath('changePassword.fields.newPassword.label')}
              disabled={isLoading}
              error={!!newPasswordError}
              hint={newPasswordError}
              {...bindNewPassword}
              maxLength={MaxLengths.Password}
            />

            <Input
              type='password'
              className={`identity__field password ${reTypePasswordError ? 'with-error' : ''}`}
              label={getContentByPath('changePassword.fields.reTypePassword.title')}
              id='reTypePassword'
              aria-label={getContentByPath('changePassword.fields.reTypePassword.label')}
              disabled={isLoading}
              error={!!reTypePasswordError}
              hint={reTypePasswordError}
              {...bindReTypePassword}
              maxLength={MaxLengths.Password}
            />
            {!isValidNewPassword && (
              <Typography variant='paragraph' className='identity__password-policy'>
                Password Requirements
                <div className='identity__password-hits-wrapper' data-testid='hints-wrapper'>
                  {passwordValidationHint(
                    'length',
                    getContentByPath('changePassword.fields.newPassword.validation.lengthHint'),
                  )}
                  {passwordValidationHint(
                    'number',
                    getContentByPath('changePassword.fields.newPassword.validation.numberHint'),
                  )}
                  {passwordValidationHint(
                    'uppercase',
                    getContentByPath('changePassword.fields.newPassword.validation.uppercaseHint'),
                  )}
                  {passwordValidationHint(
                    'lowercase',
                    getContentByPath('changePassword.fields.newPassword.validation.lowercaseHint'),
                  )}
                  {passwordValidationHint(
                    'specialCharacter',
                    getContentByPath('changePassword.fields.newPassword.validation.specialCharacterHint'),
                  )}
                  {passwordValidationHint(
                    'firstName',
                    getContentByPath('changePassword.fields.newPassword.validation.firstName'),
                  )}
                  {passwordValidationHint(
                    'lastName',
                    getContentByPath('changePassword.fields.newPassword.validation.lastName'),
                  )}
                </div>
              </Typography>
            )}
            {allRequirementsMet && (
              <Typography variant='paragraph' className='identity__password-policy' testId='reset-password-all-met'>
                {getContentByPath('changePassword.fields.newPassword.validation.allMet')}
              </Typography>
            )}
            <Button
              sticky
              ariaLabel={getContentByPath('changePassword.changePassword')}
              onClick={resetPassword}
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  resetPassword(e);
                }
              }}
              disabled={!allRequirementsMet || isLoading}
              className='form-buttom identity__form-submit'
              testId='change-password'
              type='submit'
              loading={isLoading}
            >
              <Typography>{getContentByPath('changePassword.changePassword')}</Typography>
            </Button>
            <Typography variant='body' className='identity__small-text'>
              <Link
                className='identity__activate-link'
                href={defaultRedirect}
                onClick={async () => {
                  await trackEvent(
                    'identity – update password - back to account',
                    'back to account',
                    'updatepbacktoaccount',
                    {},
                    true,
                  );
                }}
              >
                {getContentByPath('changePassword.backToAccount')}
              </Link>
            </Typography>
          </form>
        </Container>
      </PageLayout>
    </StyledContent>
  );
}

ChangePassword.displayName = 'ChangePassword';

export const Centered = styled.div`
  margin: auto;
`;
